import styles from "~/sass/organisms/Article/Article.module.scss";
import Head from "~/components/Meta/Head";
import ArticleAuthor from "~/components/molecules/Article/ArticleAuthor.js";
import Script from "next/script";

export default function RecyclingArticle({}) {
  const content = () => {
    return (
      <>
        <h1 className={styles["Article__heading"]}>
          A Family's Guide to Recycling at Home
        </h1>
        <ArticleAuthor />
        <p>
          Many of the conveniences that people across the country enjoy daily
          have a harmful impact on the environment. From the overuse of natural
          resources to the massive disposal of non-biodegradable trash, the
          habits of today's society will negatively affect coming generations.
          Fortunately, there are ways that every family can lessen their
          contribution to this problem. From small children to grandparents,
          people should come to understand how their actions hurt the
          environment and what steps they can take to better care for the
          planet.
        </p>
        <h2 className={styles["Article__sub-heading"]}>What Is Recycling?</h2>
        <p>
          Recycling is a process that turns a used item that would normally go
          into the trash into something new. Most people are familiar with
          recycling their glass bottles and plastics. Besides glass and plastic,
          there are a wide range of items in one's home that are recyclable.
          Some common recyclable items include:
        </p>
        <ul>
          <li>
            Aluminum and other{" "}
            <a
              rel="nofollow"
              href="https://www.thebalancesmb.com/an-introduction-to-metal-recycling-4057469"
            >
              metals
            </a>
          </li>
          <li>Paper</li>
          <li>Cardboard</li>
          <li>Electronics</li>
          <li>Textiles</li>
        </ul>
        <p>
          <a
            rel="nofollow"
            href="https://www.epa.gov/recycle/recycling-basics#Benefits"
          >
            Recycling benefits
          </a>{" "}
          both the environment and the economy. When families recycle, they are
          fighting against climate change by preserving natural resources and by
          reducing both the use of new raw materials and the consumption of
          energy. By recycling, people are improving the quality of the air that
          they breathe and reducing the amount of garbage that ends up in
          landfills and the ocean.
        </p>
        <h2 className={styles["Article__sub-heading"]}>Reusing vs. Reducing</h2>
        <p>
          Another way that families can help protect the environment is to{" "}
          <a
            rel="nofollow"
            href="https://lessismore.org/materials/30-reuse-tips/"
          >
            reuse
          </a>{" "}
          and reduce. While both actions lessen the amount of waste that people
          create, they do so in different ways.
        </p>
        <p>
          Reusing, as the name suggests, means to reuse an item versus throwing
          it away. For example, a child returning to school may reuse their
          backpack from the previous year instead of buying a new one. People
          can also buy items that are reusable to replace similar items that are
          disposable.
        </p>
        <p>
          Another form of reusing goods is called{" "}
          <a
            rel="nofollow"
            href="https://www.investopedia.com/terms/r/repurposing.asp"
          >
            repurposing
          </a>
          . This means giving a used item a new purpose once it can no longer be
          used for its original intent. An example of repurposing would be to
          turn an old shirt into a tote bag for carrying groceries home.
        </p>
        <p>
          Reducing involves buying only things that are necessary and decreasing
          the amount of natural resources that one's family uses at home and
          throughout the day. When a person reduces, they are also creating less
          waste. One way to do that is to avoid items that are{" "}
          <a
            rel="nofollow"
            href="https://www.wwf.org.au/news/blogs/10-worst-single-use-plastics-and-eco-friendly-alternatives"
          >
            single-use
          </a>{" "}
          and that use excess packaging materials. Another way to{" "}
          <a
            rel="nofollow"
            href="https://dnr.mo.gov/waste-recycling/reduce-reuse-recycle/reduce"
          >
            reduce waste
          </a>{" "}
          is to purchase goods that are recyclable or made using recycled
          materials.
        </p>
        <h2 className={styles["Article__sub-heading"]}>
          Do's and Don'ts of Recycling
        </h2>
        <p>
          Improper recycling is a problem that can increase costs, decrease
          efficiency, and significantly hinder a city or state's recycling
          efforts. Putting{" "}
          <a
            rel="nofollow"
            href="https://www.rd.com/article/what-happens-when-wrong-thing-recycling-bin/"
          >
            the wrong things in recycling bins
          </a>{" "}
          can also clog or break recycling equipment, create extra work, and be
          harmful to both the workers and the environment.
        </p>
        <p>
          Everyone who recycles must understand what they should and should not
          do. To better understand the recycling rules for their area, people
          can check with their city's recycling service.
        </p>
        <p>People should recycle:</p>
        <ul>
          <li>Water and beverage bottles with lids separated</li>
          <li>Juice and milk cartons and jugs</li>
          <li>Shampoo and soap bottles</li>
          <li>Cardboard packaging, making sure to flatten any boxes</li>
          <li>Paper, even if it contains staples</li>
          <li>Rinsed food cans</li>
          <li>
            <a rel="nofollow" href="https://www.gpi.org/glass-recycling-facts">
              Glass
            </a>
            , with caps removed from bottles
          </li>
          <li>
            E-waste, which must go to centers that accept this type of waste and
            not put out curbside with other recyclables
          </li>
          <li>
            Textiles, which must be given to a textile recycler and not put out
            curbside
          </li>
        </ul>
        <p>People should not recycle:</p>
        <ul>
          <li>Plastic cups, plates, or utensils</li>
          <li>
            <a
              rel="nofollow"
              href="https://blog.nationalgeographic.org/2018/04/04/7-things-you-didnt-know-about-plastic-and-recycling/"
            >
              Plastic
            </a>{" "}
            bags, as they can get stuck in the equipment and may cause it to
            break
          </li>
          <li>Batteries</li>
          <li>Plastic hangers</li>
          <li>
            <a
              rel="nofollow"
              href="https://www.yahoo.com/news/recycle-pizza-boxes-answer-might-193400254.html"
            >
              Pizza boxes
            </a>{" "}
            or other cardboard with food on it
          </li>
          <li>Styrofoam</li>
          <li>Any recyclable item that has food inside or on it</li>
          <li>Broken glass</li>
          <li>Auto parts</li>
          <li>Light bulbs and fluorescent tubes</li>
          <li>Mirrors</li>
          <li>Yard waste, including rocks</li>
        </ul>
        <p>
          Trash should also never be tossed in with recyclables. The presence of
          trash can contaminate an entire load of recyclables and turn them into
          the garbage. Some cities even charge people a fine for this offense.
        </p>
        <h2 className={styles["Article__sub-heading"]}>
          Family Tips for Recycling, Reducing, and Reusing
        </h2>
        <p>
          Recycling can and should be a family effort. Not only can families
          work together to help protect the environment, but it is also an
          effective way for both children and parents to learn more about how
          their actions affect the world around them.
        </p>
        <p>
          Recycling, reducing, and reusing aren't difficult to do and can
          quickly become second nature for all. A few easy ways to get started
          include:
        </p>
        <ul>
          <li>Consider how an item can be reused before throwing it away.</li>
          <li>
            Second-guess all purchases by asking if it is something that's
            really needed.
          </li>
          <li>Get creative with reusing items in new ways.</li>
          <li>
            Avoid wasting water by turning off the faucet while brushing one's
            teeth or taking shorter showers.
          </li>
          <li>
            Make recycling more convenient by setting up recycling bins not only
            in the kitchen but throughout the (home).
          </li>
          <li>
            Save electricity by turning off lights and unplugging cellphone
            chargers, televisions, and any device or appliance that continues to
            consume power when turned off.
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://green.harvard.edu/tools-resources/how/6-ways-minimize-your-e-waste"
            >
              Minimize e-waste
            </a>{" "}
            as much as possible by protecting and extending the life of current
            electronics. Some ways to do this include keeping cellphones in
            their protective case and not overcharging them.
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>
          Recycling Projects for Kids
        </h2>
        <p>
          Recycling risks feeling like a chore for most kids, which can make
          participation difficult. Parents can find creative ways to make it fun
          and interesting with quick and easy{" "}
          <a rel="nofollow" href="https://www.youtube.com/watch?v=xs2Z704q0wc">
            crafts
          </a>{" "}
          that involve reusing old and unwanted materials lying around the
          house. Encourage older kids with recycling projects that give them an
          immediate sense of accomplishment.
        </p>
        <p>
          <a
            rel="nofollow"
            href="https://www.waterford.org/resources/recycled-crafts/"
          >
            Recycled crafts
          </a>{" "}
          and projects for kids or the whole family include:
        </p>
        <ul>
          <li>
            Turning a water bottle or milk jug into a bird feeder or a hanging
            planter
          </li>
          <li>
            Creating jewelry using recycled materials, such as{" "}
            <a
              rel="nofollow"
              href="https://guides.brit.co/guides/make-pop-tab-bracelets"
            >
              soda can tab bracelets
            </a>
          </li>
          <li>
            Encouraging schools to start a{" "}
            <a
              rel="nofollow"
              href="http://www.greeneducationfoundation.org/nationalgreenweeksub/start-recycling.html"
            >
              school recycling program
            </a>{" "}
            for students and teachers
          </li>
        </ul>
        <h2>Other Recycling Resources</h2>
        <ul>
          <li>
            <a
              rel="nofollow"
              href="https://www.colorado.edu/ecenter/why-should-we-reduce-waste"
            >
              Why Should We Reduce Waste?
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://carrington.edu/blog/seven-ways-to-reuse-your-recyclables/"
            >
              Seven Ways to Reuse Your Recyclables
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://digitalcommons.usu.edu/cgi/viewcontent.cgi?article=2528&amp;context=extension_curall"
            >
              Reuse: Creating a Next Life for Common Items
            </a>
          </li>
          <li>
            <a rel="nofollow" href="https://cco.ku.edu/planter-flowerpot-diy/">
              Planter/Flower Pot DIY
            </a>
          </li>
          <li>
            <a rel="nofollow" href="https://www.asdk12.org/Page/7943">
              Refuse, Reduce, Reuse, Rot, Recycle, and Rethink
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.plt.org/family-activity/reuse-and-recycle"
            >
              Reuse and Recycle at{" "}
            </a>
            <a href="https://www.realestateagents.com/nh/manchester">Home</a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://pbskids.org/video/sid-science-kid/1884201189"
            >
              Recycling With Sid the Science Kid
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.anchorpreschool.org/amp/the-three-r-s-to-recycling-how-to-reduce-reuse-and-recycle"
            >
              The Three R's to Recycling: How to Reduce, Reuse, and Recycle
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.recycleacrossamerica.org/tips-to-recycle-right"
            >
              Let's Reduce Waste and Recycle Right
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.republicservices.com/blog/7-tips-and-tricks-to-simplify-recycling"
            >
              Seven Tips and Tricks to Simplify Recycling
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.nature.org/en-us/about-us/where-we-work/united-states/delaware/stories-in-delaware/delaware-eight-ways-to-reduce-waste/"
            >
              Eight Ways to Reduce Waste
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.addisoncountyrecycles.org/recycling/reduce-reuse/plastics-reduction"
            >
              Beginner's Guide to Reducing Waste
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://frederickcountymd.gov/DocumentCenter/View/332095/Life-Less-Plastic"
            >
              Plastic Swaps: Ideas for Living With Less Plastic
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.todaysparent.com/family/family-life/ways-to-reduce-waste-at-home-even-if-you-are-exhausted/"
            >
              25 Ways to Reduce Waste at{" "}
            </a>
            <a href="https://www.realestateagents.com/nh/concord">Home</a>
            <a
              rel="nofollow"
              href="https://www.todaysparent.com/family/family-life/ways-to-reduce-waste-at-home-even-if-you-are-exhausted/"
            >
              , Even if You're Exhausted
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://content.ces.ncsu.edu/before-you-recycle-choose-to-reuse"
            >
              Before You Recycle, Choose to Reuse
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.hennepin.us/choose-to-reuse/tips/replace-with-reusables"
            >
              Replacing Disposables With Reusables
            </a>
          </li>
        </ul>
      </>
    );
  };

  return (
    <>
      <Head
        title={"A Family's Guide to Recycling at Home | RealEstateAgents.com"}
        description={
          "Recycling specifics have you confused? Become an expert and reduce your footprint with this helpful guide."
        }
      />
      <article className={styles["Article"]}>
        <div className={styles["Article__container"]}>{content()}</div>
      </article>
      <Script strategy="beforeInteractive" type="application/ld+json">
        {{
          "@context": "https://schema.org",
          "@type": "Article",
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id":
              "https://www.realestateagents.com/articles/recycling-at-home",
          },
          headline: "A Family's Guide to Recycling at Home",
          author: {
            "@type": "Person",
            name: "Amber Martinez",
            jobTitle: "Education Writer",
            sameAs: [
              "https://www.realestateagents.com/articles/author/amber-martinez",
              "https://www.linkedin.com/in/amber-martinez-7096651a/",
            ],
          },
        }}
      </Script>
    </>
  );
}
